import { rpx } from 'client/lib/rpx-client';
import { LoadedProps, RouteLoadProps, defRoute } from '@components/router';
import { ErrorPage } from '@components/error-page';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { CampaignPageShell } from '@course/components/campaign/campaign-page-shell';
import { SectionRenderer } from '@course/components/campaign/section-renderer';
import { PurchaseFn } from 'shared/payments/types';
import { useOntraportAccountOverride } from 'client/lib/hooks/use-ontraport-account-override';
import { detectTimezone } from 'shared/dateutil';

export const route = defRoute({
  isPublic: true,
  load,
  Page,
  disableSubrouteScroll: true,
  async loadSubroute(opts, setState) {
    const result = await load(opts);
    setState(result);
  },
});

async function load(route: RouteLoadProps) {
  const searchParams = new URLSearchParams(location.search);
  const result = await rpx.courseCheckout.getCampaignPage({
    slug: route.params.slug,
    priceId: searchParams.get('price') || undefined,
    couponId: searchParams.get('coupon') || undefined,
    // Only used to pick which currency shows first, and only when the visitor
    // has not asked for a price themselves.
    timezone: detectTimezone(),
  });
  if ('redirectTo' in result && result.redirectTo) {
    window.location.replace(result.redirectTo as string);
    return result as any;
  }
  return {
    ...result,
    testModeToken: searchParams.get('campaign_test') || undefined,
  };
}

type Props = LoadedProps<typeof load>;

function Page(props: Props) {
  if ('redirectTo' in props.state) {
    return null;
  }

  const { course, config, price, coupon, collectAddress, alternativePrices, aipBackupOffer } =
    props.state;
  const testModeToken = props.state.testModeToken;
  const testMode = !!testModeToken;

  useOntraportAccountOverride(course.ontraportAccountId);

  const pageTitle = config.meta?.pageTitle || course.title;
  useDocumentTitle([pageTitle], { siteName: false });

  if (!price) {
    return <ErrorPage title="404 | Not Found" />;
  }

  const purchaseCourse: PurchaseFn = async (opts) => {
    const affiliateRaw = sessionStorage.getItem('affiliate_tracking');
    const affiliateParsed = affiliateRaw ? JSON.parse(affiliateRaw) : undefined;
    const affiliateCode = affiliateParsed?.aff || affiliateParsed?.orid;

    return rpx.courseCheckout.purchase({
      ...opts,
      courseId: course.id,
      courseTitle: course.title,
      priceId: price.id,
      couponId: testMode ? undefined : coupon?.id,
      affiliateCode,
      testModeToken,
    });
  };

  const onPurchaseComplete = async (opts?: { newUser?: boolean }) => {
    const params = new URLSearchParams({ priceId: price.id });
    if (opts?.newUser) {
      params.set('newuser', 'true');
    }
    await props.router.redirectTo(`/courses/${course.id}/provision?${params}`);
  };

  return (
    <CampaignPageShell brand={config.brand}>
      {testMode && (
        <div style="position:sticky;top:0;z-index:50;background:#fef3c7;color:#78350f;padding:8px 16px;text-align:center;font-weight:600;font-size:14px;border-bottom:1px solid #fcd34d;">
          TEST MODE — This purchase will charge $1 regardless of the listed price.
        </div>
      )}
      <SectionRenderer
        sections={config.sections}
        brand={config.brand}
        checkoutProps={{
          courseId: course.id,
          price,
          coupon: testMode ? undefined : coupon,
          collectAddress,
          beginPurchase: purchaseCourse,
          onPurchaseComplete,
          alternativePrices,
          campaignSlug: props.params.slug as string,
          testModeToken,
          aipBackupOffer: testMode ? undefined : aipBackupOffer,
          simpleCheckConsent: !!config.simpleCheckConsent,
        }}
      />
    </CampaignPageShell>
  );
}
